import { useEffect, useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Phone, Users, Search } from "lucide-react";
import { useParams } from "react-router";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import clsx from "clsx";
import InnerLoader from "../../../component/ui/loader/inner-loader";

export default function KeywordDetailsPage() {
    const { id } = useParams();
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [speakerFilter, setSpeakerFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const apiCall = useApiCall();

    const getTranscriptDetail = async () => {
        try {
            setLoading(true);
            const res = await apiCall({ url: `get-keyword-details?keyword_id=${id}`, method: "get" });
            if (res?.success) setData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) getTranscriptDetail();
    }, [id]);

    // dynamically get all speakers
    const allSpeakers = useMemo(() => {
        const speakers = new Set();
        data.forEach(item => item.conversations.forEach(conv => speakers.add(conv.speaker_name)));
        return ["All", ...Array.from(speakers).sort()]; // sort alphabetically
    }, [data]);

    // Filter conversations based on search and speaker filter
    const filteredData = useMemo(() => {
        return data
            .map(item => ({
                ...item,
                conversations: item.conversations.filter(conv => {
                    const matchesSearch = conv.text.toLowerCase().includes(search.toLowerCase());
                    const matchesSpeaker = speakerFilter === "All" || conv.speaker_name === speakerFilter;
                    return matchesSearch && matchesSpeaker;
                })
            }))
            .filter(item => item.conversations.length > 0);
    }, [data, search, speakerFilter]);

    // Header stats
    const totalCalls = data.filter(d => d.source === "Call").length;
    const totalMeetings = data.filter(d => d.source !== "Call").length;
    const totalMentions = data.reduce((acc, call) => acc + (call.conversations?.length || 0), 0);
    const keyword = data?.[0]?.keyword || "Keyword";

    return (
        <div className="h-screen w-full bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex flex-col gap-6 overflow-hidden">
            {/* Loader */}
            {loading && <InnerLoader />}

            {!loading && (

                <>
                    {/* Header */}
                    <div className="bg-white rounded-xl shadow p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-semibold capitalize">
                                Keyword: <span className="text-blue-600">{keyword}</span>
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">See where and how this keyword was mentioned</p>
                        </div>
                        <div className="flex gap-4 flex-wrap">
                            <Stat label="Total Calls" value={totalCalls} />
                            <Stat label="Total Meetings" value={totalMeetings} />
                            <Stat label="Total Mentions" value={totalMentions} />
                        </div>
                    </div>

                    {/* Search & Filters */}
                    <div className="bg-white rounded-xl shadow p-4 flex items-center gap-2 relative">
                        <Search className="w-5 h-5 text-gray-400" />
                        <input
                            placeholder="Search conversations..."
                            className="w-full outline-none text-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        {/* Custom Dropdown for Speakers */}
                        {/* <div className="relative">
                    <button 
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="flex items-center gap-1 border border-gray-300 px-3 py-1 rounded cursor-pointer bg-white hover:bg-gray-50"
                    >
                        <span>
                            {speakerFilter === "All" ? "All Speakers" : `${speakerFilter} Speaker`}

                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    </button>

                    {dropdownOpen && (
                        <ul className="absolute right-0 mt-1 bg-white shadow-lg rounded w-40 max-h-60 overflow-auto z-50">
                            {allSpeakers.map((speaker, idx) => (
                                <li
                                    key={idx}
                                    className={clsx(
                                        "px-3 py-2 cursor-pointer hover:bg-blue-100",
                                        speaker === speakerFilter && "bg-blue-50 font-semibold"
                                    )}
                                    onClick={() => {
                                        setSpeakerFilter(speaker);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    {speaker} Speaker
                                </li>
                            ))}
                        </ul>
                    )}
                </div> */}
                    </div>

                    {/* Records */}
                    <div className="space-y-4 w-full h-full overflow-auto pr-2">
                        {filteredData.length > 0 ? (
                            filteredData.map((item) => <RecordAccordion key={item.source_id} item={item} />)
                        ) : (
                            <p className="text-center text-gray-400 mt-10">No matching conversations found.</p>
                        )}
                    </div>

                </>

            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-gray-50 px-4 py-2 rounded-lg text-center min-w-[120px]">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-800">{value}</p>
        </div>
    );
}

function RecordAccordion({ item }) {
    const [open, setOpen] = useState(true);

    return (
        <div className="bg-white rounded-xl shadow overflow-hidden transition-all duration-300 hover:shadow-lg">
            <div
                onClick={() => setOpen(!open)}
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        {item.source === "Call" ? (
                            <Phone className="w-5 h-5 text-blue-600" />
                        ) : (
                            <Users className="w-5 h-5 text-blue-600" />
                        )}
                    </div>
                    <div>
                        <h3 className="font-medium text-gray-800">{item.source} #{item.source_id}</h3>
                        <div className="flex gap-2 text-xs text-gray-500 mt-1">
                            <span className="px-2 py-0.5 rounded-full">Mentions: {item.conversations.length}</span>
                            {/* <span className="bg-blue-100 px-2 py-0.5 rounded-full">{item.source}</span> */}
                        </div>
                    </div>
                </div>
                {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>

            {open && (
                <div className="p-4 bg-gray-50 space-y-3 animate-fadeIn">
                    {item.conversations.map((conv, i) => (
                        <ConversationBubble key={i} conv={conv} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ConversationBubble({ conv }) {
    return (
        <div
            className={clsx(
                "p-4 rounded-lg border text-sm leading-relaxed",
                conv.speaker_name === "A"
                    ? "bg-blue-50 border-blue-100"
                    : conv.speaker_name === "B"
                        ? "bg-green-50 border-green-100"
                        : "bg-gray-100 border-gray-200"
            )}
        >
            <div className="mb-1 text-xs font-semibold uppercase text-gray-500">Speaker {conv.speaker_name}</div>
            <p className="text-gray-700">{conv.text}</p>
        </div>
    );
}


function highlightKeyword(text, keyword) {
    if (!keyword) return text;

    // Escape special regex characters in keyword
    const escapedKeyword = keyword.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

    // Create regex for all matches, case-insensitive
    const regex = new RegExp(`(${escapedKeyword})`, "gi");

    // Replace matches with span for highlighting
    return text.replace(
        regex,
        `<span class="bg-yellow-200 px-1 rounded">$1</span>`
    );
}

